import { Modal, TextInput, Select, Group, Button, Stack, Text, ScrollArea } from '@mantine/core';
import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { User, UserRole } from '../../../../models/user.model';
import { createUser, updateUser } from '../../../../redux/reducers/userSlice';
import { selectUserInfo } from '../../../../redux/selectors/authSelector';
import { useMediaQuery } from '@mantine/hooks';

interface AddEditUserProps {
  opened: boolean;
  onClose: () => void;
  activeUser: User | null;
}

function AddEditUser({ opened, onClose, activeUser }: AddEditUserProps) {
  const currentUser = useSelector(selectUserInfo);
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 50em)');
  const emptyUser: Partial<User> = {
    first_name: '',
    last_name: '',
    email: '',
    role: UserRole.USER,
    password: '',
  };
  const [user, setUser] = useState<Partial<User>>(emptyUser);

  const userRoleOptions = useMemo(() => {
    const options = Object.values(UserRole)
    .filter(role => {
      if (currentUser?.role === UserRole.ADMIN) {
        return true;
      }
      return role !== UserRole.ADMIN;
    })
    .map(role => ({ label: role.charAt(0).toUpperCase() + role.slice(1), value: role }));
    return options;
  }, [currentUser]);

  const handleSubmit = () => {
    if (user.first_name && user.last_name && user.email && user.role) {
      if (activeUser) {
        const userData = {
          first_name: user.first_name,
          last_name: user.last_name,
          email: user.email,
          role: user.role,
        };
        dispatch(updateUser({
          id: activeUser._id,
          userData: !user.password ? userData : {
            ...userData,
            password: user.password,
          }
        } as any));
      } else {
        dispatch(createUser(user as any));
      }
      onClose();
      setUser(emptyUser);
    }
  };

  useEffect(() => {
    if (activeUser) {
      setUser({
        first_name: activeUser.first_name,
        last_name: activeUser.last_name,
        email: activeUser.email,
        role: activeUser.role,
      });
    } else {
      setUser(emptyUser);
    }
  }, [activeUser]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={activeUser ? 'Edit User' : 'Add New User'}
      size="lg"
      radius="md"
      fullScreen={isMobile}
      transitionProps={{ transition: 'fade', duration: 200 }}
      scrollAreaComponent={ScrollArea.Autosize}
      removeScrollProps={{ allowPinchZoom: true }}
    >
      <Stack gap="md">
        <TextInput
          label="First Name"
          placeholder="Enter first name"
          value={user.first_name}
          onChange={(e) => setUser({ ...user, first_name: e.target.value })}
          required
          minLength={3}
        />
        <TextInput
          label="Last Name"
          placeholder="Enter last name"
          value={user.last_name}
          required
          onChange={(e) => setUser({ ...user, last_name: e.target.value })}
          minLength={3}
        />
        <TextInput
          label="Email"
          placeholder="Enter email"
          value={user.email}
          onChange={(e) => setUser({ ...user, email: e.target.value })}
        />
        <Select
          label="Role"
          placeholder="Select role"
          data={userRoleOptions}
          value={user.role}
          onChange={(value) => setUser({ ...user, role: value as UserRole })}
        />

        <TextInput
          label="Password"
          placeholder="Enter password"
          value={user.password}
          onChange={(e) => setUser({ ...user, password: e.target.value })}
        />
        <Text size="sm" c="dimmed">Warning: This password will override the existing password.</Text>

      </Stack>
      <Group justify="flex-end" mt="md">
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit}>{activeUser ? 'Update User' : 'Create User'}</Button>
      </Group>
    </Modal>
  );
}

export default AddEditUser;
